import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermission } from '~/hooks/usePermission';
import { api } from '~/redux/api/createApi';
import { RoleAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';

type Props = {
  role: RoleAttributes;
  isVisible: boolean;
  isDefaultRole?: boolean;
  clientId?: string;
  onClose: () => void;
};

export const DeleteRoleModal = ({
  role,
  isVisible,
  isDefaultRole,
  clientId,
  onClose,
}: Props) => {
  const intl = useIntl();
  const user = useCurrentUser();

  const [deleteRole, deleteRoleResult] = api.endpoints.deleteRole.useMutation();
  const { isLoading } = deleteRoleResult;
  const [
    getRoles,
    { isFetching: isFetchingRoles, data: roles, isLoading: isLoadingRoles },
  ] = api.endpoints.getRolesSimple.useLazyQuery();

  const [selectedRole, setSelectedRole] = useState<Nullable<string>>('');
  const [defaultRole, setDefaultRole] = useState<Nullable<string>>('');

  const { hasPermission: hasSSOPermission, isLoading: isLoadingSSOPermission } =
    usePermission({
      permission: { resourceId: 'sso', resourceType: 'edit' },
    });

  const {
    hasPermission: hasUsersManagePermission,
    isLoading: isLoadingUsersManagePermission,
  } = usePermission({
    permission: { resourceId: 'users', resourceType: 'edit' },
  });

  const roleOptions = useMemo(
    () =>
      roles?.results
        .filter((roleItem) => roleItem.id !== role.id)
        .map(roleAsSelectOption) || [],
    [roles?.results, role],
  );

  const handleSearchRole = useDebounce((search: string) => {
    getRoles({
      page: 1,
      pageSize: 100,
      name: search,
      client_id: clientId,
    });
  }, INPUT_DEBOUNCE_MS);

  const shouldShowNoSSOPermissionWarning =
    isDefaultRole && !hasSSOPermission && !isLoadingSSOPermission;

  const shouldShowManageUsersPermissionWarning =
    !hasUsersManagePermission && !isLoadingUsersManagePermission && role.users;

  const isButtonDisabled = useMemo(() => {
    if (isDefaultRole && !hasSSOPermission) {
      return true;
    }

    if (role.users && isDefaultRole) {
      return !selectedRole || !defaultRole;
    }
    if (role.users) {
      return !selectedRole;
    }
    if (isDefaultRole) {
      return !defaultRole;
    }

    return false;
  }, [role, isDefaultRole, selectedRole, defaultRole]);

  const placeholder = intl.formatMessage({
    id: 'DeleteRoleModal.selectRolePlaceholder',
    defaultMessage: 'Select a role...',
    description: 'Placeholder for role selection field',
  });

  const noUsersManagePermissionWarningText = intl.formatMessage({
    id: 'DeleteRoleModal.noManageUsersPermissionWarning',
    defaultMessage:
      'This role cannot be deleted because it is currently assigned to active users. Please contact an Admin user with permissions to manage user roles to reassign active users to another role before deleting this role.',
    description: 'Warning message when user lacks permissions to manage users',
  });

  const tooltipText = useMemo(() => {
    if (shouldShowManageUsersPermissionWarning) {
      return noUsersManagePermissionWarningText;
    }

    if (role.users && isDefaultRole) {
      return intl.formatMessage({
        id: 'DeleteRoleModal.selectReplacementForBothTooltip',
        defaultMessage:
          'Select a replacement role for the SSO default role and the users that are currently assigned to this role.',
        description:
          'Tooltip when both user role and default role need to be selected',
      });
    }
    if (role.users) {
      return intl.formatMessage({
        id: 'DeleteRoleModal.selectReplacementForUsersTooltip',
        defaultMessage:
          'Select a replacement role for the users that are currently assigned to this role.',
        description: 'Tooltip when user role needs to be selected',
      });
    }
    if (isDefaultRole) {
      return intl.formatMessage({
        id: 'DeleteRoleModal.selectReplacementForDefaultTooltip',
        defaultMessage: 'Select a replacement role for the SSO default role.',
        description: 'Tooltip when default role needs to be selected',
      });
    }

    return '';
  }, [
    role,
    isDefaultRole,
    selectedRole,
    defaultRole,
    intl,
    shouldShowManageUsersPermissionWarning,
    noUsersManagePermissionWarningText,
  ]);

  const handleSuccess = () => {
    showToast(
      SUCCESS,
      intl.formatMessage({
        id: 'DeleteRoleModal.successMessage',
        defaultMessage: 'Role deleted successfully.',
        description: 'Success message when role is deleted',
      }),
    );
    onClose();
  };

  const handleError = (error: any) => {
    const resp = coerceRtkqError(error)?.response || {};
    const msg = resp.data?.errors?.[0];
    showToast(
      ERROR,
      msg?.title ||
        intl.formatMessage({
          id: 'DeleteRoleModal.errorMessage',
          defaultMessage: 'An error occurred while deleting the role.',
          description: 'Error message when role deletion fails',
        }),
    );
  };

  useEffect(() => {
    getRoles({
      page: 1,
      pageSize: 100,
      client_id: clientId,
    });
  }, []);

  return (
    <Modal
      isVisible={isVisible}
      title={intl.formatMessage(
        {
          id: 'DeleteRoleModal.modalTitle',
          defaultMessage: 'Delete {roleName}',
          description:
            'Title of the delete role modal. Example: roleName = "Admin"',
        },
        {
          roleName: role.name,
        },
      )}
      onCancel={onClose}
      onHide={onClose}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeleteRoleModal.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        }),
        onClick: async () => {
          trackSegment('submitDeleteRole', {
            id: role.id,
            name: role.name,
            user_id: user.id,
            client_id: user.client,
          });
          try {
            await deleteRole({
              roleId: role.id,
              newRoleId: selectedRole || undefined,
              defaultRoleId: defaultRole || undefined,
            }).unwrap();
            handleSuccess();
          } catch (error) {
            handleError(error);
          }
        },
        isLoading:
          isLoading ||
          isLoadingRoles ||
          isLoadingSSOPermission ||
          isLoadingUsersManagePermission,
        disabled: isButtonDisabled,
        tooltip: isButtonDisabled ? tooltipText : undefined,
        variant: 'danger',
      }}
    >
      {role.users ? (
        <>
          {isDefaultRole ? (
            <Layout direction="column" spacing="1">
              <Text>
                <FormattedMessage
                  id="DeleteRoleModal.defaultRoleWithUsersMessage"
                  defaultMessage="The <strong>{roleName}</strong> role is assigned to {userCount, plural, one {# user} other {# users}} and is the default role for new users signing in with SSO. Before permanently deleting it, you need to:"
                  description="Message when deleting default role with users. Example: roleName = 'Administrator', userCount = 5"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    roleName: role.name,
                    userCount: role.users,
                  }}
                />
              </Text>
              <Text>
                {intl.formatMessage(
                  {
                    id: 'DeleteRoleModal.step1Message',
                    defaultMessage:
                      '1. Select a replacement role for the {userCount} user(s)',
                    description:
                      'Step 1 message for role deletion. Example: userCount = "5"',
                  },
                  {
                    userCount: role.users,
                  },
                )}
              </Text>
              <Text>
                {intl.formatMessage({
                  id: 'DeleteRoleModal.step2Message',
                  defaultMessage:
                    '2. Select a replacement default role for new users signing in with SSO',
                  description: 'Step 2 message for role deletion',
                })}
              </Text>
            </Layout>
          ) : (
            <Text>
              <FormattedMessage
                id="DeleteRoleModal.roleWithUsersMessage"
                defaultMessage="Select a new role for the {userCount} users that are currently assigned to <strong>{roleName}</strong>. Deleting a role is permanent."
                description="Message when deleting role with users. Example: userCount = '5', roleName = 'Document Name'"
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  userCount: role.users,
                  roleName: role.name,
                }}
              />
            </Text>
          )}

          <Layout direction="column" spacing="1" mt={6}>
            <FormField
              label={intl.formatMessage({
                id: 'DeleteRoleModal.userRoleLabel',
                defaultMessage: 'User role',
                description: 'Label for user role selection field',
              })}
              description={intl.formatMessage(
                {
                  id: 'DeleteRoleModal.userRoleDescription',
                  defaultMessage:
                    'Select a replacement role for the {userCount} user(s).',
                  description:
                    'Description for user role selection field. Example: userCount = "5"',
                },
                {
                  userCount: role.users,
                },
              )}
              tooltip={
                shouldShowManageUsersPermissionWarning
                  ? noUsersManagePermissionWarningText
                  : undefined
              }
              input={Select}
              inputProps={{
                onSearch: handleSearchRole,
                isMulti: false,
                isSearchable: true,
                options: roleOptions,
                isLoading: isFetchingRoles,
              }}
              placeholder={placeholder}
              name={intl.formatMessage({
                id: 'DeleteRoleModal.userRolePlaceholder',
                defaultMessage: 'User role',
                description: 'Name of the user role selection field',
              })}
              value={
                shouldShowManageUsersPermissionWarning
                  ? undefined
                  : selectedRole
              }
              onChange={(value) => setSelectedRole(value as string)}
              disabled={!!shouldShowManageUsersPermissionWarning}
            />
          </Layout>
          {isDefaultRole && (
            <Layout
              direction="column"
              spacing="1"
              mt={4}
              aria-labelledby="sso-default-label"
            >
              <Text variant="body-bold" id="sso-default-label">
                {intl.formatMessage({
                  id: 'DeleteRoleModal.ssoDefaultRoleLabel2',
                  defaultMessage: 'SSO default role',
                  description: 'Label for SSO default role field',
                })}
              </Text>

              <Text color="text.secondary">
                {intl.formatMessage({
                  id: 'DeleteRoleModal.ssoDefaultRoleDescription2',
                  defaultMessage:
                    'Select a replacement role for new users signing in with SSO.',
                  description: 'Description for SSO default role field',
                })}
              </Text>
              <Select
                placeholder={placeholder}
                isLoading={isFetchingRoles}
                options={roleOptions}
                name="SSO default role"
                value={defaultRole}
                onChange={setDefaultRole}
                onSearch={handleSearchRole}
                isMulti={false}
                isSearchable={true}
                disabled={!!shouldShowNoSSOPermissionWarning}
              />
            </Layout>
          )}
        </>
      ) : (
        <>
          {isDefaultRole ? (
            <Layout direction="column" spacing="1">
              <Text>
                <FormattedMessage
                  id="DeleteRoleModal.defaultRoleOnlyMessage"
                  defaultMessage="The <strong>{roleName}</strong> role is currently the default role for new users signing in with SSO. Select a replacement role for new users signing in with SSO. Deleting a role is permanent and it cannot be undone."
                  description="Message when deleting default role without users. Example: roleName = 'Default User'"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    roleName: role.name,
                  }}
                />
              </Text>

              <Layout
                direction="column"
                spacing="1"
                mt={6}
                aria-labelledby="sso-default-label"
              >
                <Text variant="body-bold" id="sso-default-label">
                  {intl.formatMessage({
                    id: 'DeleteRoleModal.ssoDefaultRoleLabel',
                    defaultMessage: 'SSO default role',
                    description: 'Label for SSO default role field',
                  })}
                </Text>
                <Text color="text.secondary">
                  {intl.formatMessage({
                    id: 'DeleteRoleModal.ssoDefaultRoleDescription',
                    defaultMessage:
                      'Select a replacement role for new users signing in with SSO.',
                    description: 'Description for SSO default role field',
                  })}
                </Text>
                <Select
                  placeholder={placeholder}
                  isLoading={isFetchingRoles}
                  options={roleOptions}
                  name="SSO default role"
                  value={defaultRole}
                  onChange={setDefaultRole}
                  onSearch={handleSearchRole}
                  isMulti={false}
                  isSearchable={true}
                  disabled={!!shouldShowNoSSOPermissionWarning}
                />
              </Layout>
            </Layout>
          ) : (
            <Text>
              <FormattedMessage
                id="DeleteRoleModal.confirmDeleteMessage"
                defaultMessage="Are you sure you want to permanently delete <strong>{roleName}</strong>?"
                description="Confirmation message for role deletion. Example: roleName = 'Manager'"
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  roleName: role.name,
                }}
              />
            </Text>
          )}
        </>
      )}
      {shouldShowNoSSOPermissionWarning && (
        <Layout mt={2}>
          <StatusMessage
            status="warning"
            message={intl.formatMessage({
              id: 'DeleteRoleModal.noSSOPermissionWarning',
              defaultMessage:
                'This role cannot be deleted because it is configured as the SSO default role. Please contact an Admin user with permissions to the SSO setting page to edit this configuration before deleting this role.',
              description: 'Warning message when user lacks SSO permissions',
            })}
          />
        </Layout>
      )}
    </Modal>
  );
};

const roleAsSelectOption = (role: ParsedType<RoleAttributes>) => ({
  label: role.name,
  value: role.id,
  description: role.roleType,
});
